// Why a referral contact is on the user's list.
//
// The backend (lib/referral/contactRelevance.js) scores every contact against
// the user's own resume — seniority, tenure at the company, and background
// overlap — and ships the evidence back on `contact.relevance`. That is what we
// render whenever it's present.
//
// The local heuristics below are the fallback for contacts served by older
// cached payloads that predate `relevance`, so a card never renders blank.

const STOPWORDS = new Set([
    'senior', 'junior', 'staff', 'lead', 'principal', 'head', 'director', 'vice',
    'president', 'chief', 'officer', 'manager', 'executive', 'global', 'regional',
    'the', 'and', 'for', 'sr', 'jr', 'ii', 'iii'
]);

// Crude stemmer so "engineering" ↔ "engineer", "sales" ↔ "sale" line up.
const stem = (w) => w.replace(/(ing|ers|er|s)$/, '');

const tokens = (s) => String(s || '')
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((w) => w.length > 2 && !STOPWORDS.has(w))
    .map(stem);

const relevanceOf = (contact) => contact?.relevance || contact?._fullData?.relevance || null;

/**
 * Short human-readable reason a contact is on the referral list.
 * @param {Object} contact - contact with a `title` (top-level or in _fullData)
 * @param {string} roleHint - the job/signal role title to compare against
 */
export const referralReason = (contact, roleHint = '') => {
    const scored = relevanceOf(contact);
    if (scored?.headline) return scored.headline;

    const title = String(contact?.title || contact?._fullData?.title || '').toLowerCase();
    if (!title) return 'Well-placed to refer you internally';

    if (/recruit|talent|staffing|people (ops|operations|partner)|human resources|\bhr\b/.test(title)) {
        return 'Hires for roles like this — can route your application directly';
    }

    const roleTokens = new Set(tokens(roleHint));
    const sharesDept = roleTokens.size > 0 && tokens(title).some((w) => roleTokens.has(w));
    const isExec = /chief|founder|president|\bvp\b|vice president|head of|director/.test(title);
    const isLead = /manager|lead|principal/.test(title);

    if (sharesDept && (isExec || isLead)) return 'Likely leads the team this role reports into';
    if (sharesDept) return 'Works in the same team as this role';
    if (isExec) return 'Senior decision-maker who can champion your referral';
    if (isLead) return 'Manager-level — can influence hiring decisions';
    return 'Well-placed to refer you internally';
};

/**
 * Everything the UI needs to justify one contact.
 *
 * @param {Object} contact
 * @param {string} roleHint - role title, used only by the fallback path
 * @param {number} maxReasons - how many supporting lines to return
 * @returns {{ headline: string, reasons: string[], badges: string[], tier: string|null }}
 */
export const contactEvidence = (contact, roleHint = '', maxReasons = 2) => {
    const scored = relevanceOf(contact);
    const text = (list) => (list || []).map((r) => (typeof r === 'string' ? r : r?.text)).filter(Boolean);
    if (scored) {
        return {
            headline: scored.headline || referralReason(contact, roleHint),
            reasons: text(scored.reasons).slice(0, maxReasons),
            // Concerns / tips / verdict are only rendered where there's room for
            // them (the contacts modal); compact cards stay at headline + reasons.
            concerns: text(scored.concerns),
            outreach: text(scored.outreach),
            assessment: scored.assessment || '',
            badges: scored.badges || [],
            tier: scored.tier || null
        };
    }
    return {
        headline: referralReason(contact, roleHint),
        reasons: [], concerns: [], outreach: [], assessment: '', badges: [], tier: null
    };
};

// Tier → chip colours. Kept here so both pages label priority identically.
export const TIER_STYLE = {
    best: { label: 'Top pick', color: '#047857', bg: '#ECFDF5' },
    strong: { label: 'Strong', color: '#3F57E0', bg: '#EEF1FE' },
    good: { label: 'Worth a message', color: '#B45309', bg: '#FFFBEB' },
    reach: { label: 'Long shot', color: '#64748B', bg: '#F1F5F9' }
};

export default referralReason;
